import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiCalendar, FiChevronRight, FiEdit2, FiPlus, FiSearch, FiTrash2, FiX,
    FiMapPin, FiLayers, FiAlertTriangle, FiZap, FiWifi, FiClock, FiClipboard,
} from "react-icons/fi";
import { MdCheckCircle } from "react-icons/md";
import axiosInstance from "../utils/api";
import logo from "../assets/images/expo-pilot-logo.png";
import toast from "react-hot-toast";

const defaultExcelFields = [
    { name: "Hall" },
    { name: "Stand Number" },
    { name: "Exhibitor Name" },
    { name: "Size" },
    { name: "Tent Number" },
];

const defaultActivities = [
    { key: "arrival", name: "Arrival Onsite" },
    { key: "offloading", name: "Material Offloading" },
    { key: "platformBuild", name: "Platform Build" },
    { key: "metalWork", name: "Metal Work" },
    { key: "structureBuild", name: "Structure Build" },
    { key: "painting", name: "Painting" },
    { key: "lighting", name: "Lighting Setup" },
    { key: "avSetup", name: "AV Setup" },
    { key: "graphics", name: "Graphics" },
    { key: "equipmentSetup", name: "Equipment Setup" },
    { key: "avTesting", name: "AV Testing" },
    { key: "equipmentTesting", name: "Equipment Testing" },
    { key: "furnitureSetup", name: "Furniture Setup" },
    { key: "standCleaning", name: "Stand Cleaning" },
];

const emptyForm = {
    name: "", hallsNumber: "", tentsNumber: "",
    buildUpStart: "", buildUpEnd: "",
    showDaysStart: "", showDaysEnd: "",
    teardownStart: "", teardownEnd: "",
    customFields: defaultExcelFields.map((f) => ({ ...f })),
    hasRetroPlan: false,
    activities: defaultActivities.map((a) => ({ ...a, percent: 0 })),
    electricity: true,
    internet: true,
};

const toInputDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDateRange = (start, end) => {
    const fmt = (d) => d && new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const s = fmt(start), e = fmt(end);
    if (!s && !e) return "Not set";
    return e && e !== s ? `${s} – ${e}` : s || e;
};

const getEventStatus = (event) => {
    const now = Date.now();
    const t = (d) => (d ? new Date(d).getTime() : null);
    const buildStart = t(event.buildUp?.start);
    const showStart = t(event.showDays?.start);
    const showEnd = t(event.showDays?.end);
    const tearEnd = t(event.teardown?.end);
    const end = tearEnd ?? showEnd;

    if (!buildStart && !showStart) return { label: "Draft", tone: "neutral" };
    if (end && now > end) return { label: "Ended", tone: "neutral" };
    if (showStart && showEnd && now >= showStart && now <= showEnd) return { label: "Live", tone: "green" };
    if (showEnd && now > showEnd) return { label: "Teardown", tone: "sky" };
    if (buildStart && now >= buildStart && (!showStart || now < showStart)) return { label: "Build-up", tone: "amber" };
    return { label: "Upcoming", tone: "blue" };
};

const toneClasses = {
    neutral: "bg-neutral-100 text-neutral-600 ring-neutral-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    sky: "bg-sky-50 text-sky-700 ring-sky-200",
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
};

const eventId = (e) => e?._id || e?.id;
const inputClass = "w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm text-neutral-900 placeholder-neutral-400 outline-none transition focus:border-[#051948] focus:ring-4 focus:ring-[#051948]/10";

export default function Dashboard() {
    const navigate = useNavigate();

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(null);
    const [search, setSearch] = useState("");
    const [modal, setModal] = useState(null);
    const [deletingEvent, setDeletingEvent] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [originalActivityIds, setOriginalActivityIds] = useState([]);
    const [quota, setQuota] = useState(null);

    const eventLimit = quota && quota.limit != null ? quota.limit : Infinity;

    const canCreateEvent = eventLimit === Infinity || events.length < eventLimit;
    const nearLimit = eventLimit !== Infinity && events.length >= eventLimit - 1 && events.length < eventLimit;

    useEffect(() => {
        (async () => {
            try {
                const [ev, q] = await Promise.all([
                    axiosInstance.get("/event/user"),
                    axiosInstance.get("/auth/quota"),
                ]);
                const data = ev.data;
                setEvents(Array.isArray(data) ? data : data?.data || data?.events || []);
                setQuota(q.data);
            } catch (err) {
                console.error("Failed to fetch events:", err.response?.data || err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filteredEvents = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? events.filter((e) => e.name?.toLowerCase().includes(q)) : events;
    }, [events, search]);

    const openCreate = () => {
        if (!canCreateEvent) return;
        setOriginalActivityIds([]);
        setForm({
            ...emptyForm,
            customFields: defaultExcelFields.map((f) => ({ ...f })),
            activities: defaultActivities.map((a) => ({ ...a, percent: 0 })),
        });
        setModal({ mode: "create" });
    };

    const openEdit = async (event) => {
        const id = eventId(event);
        setLoadingEdit(id);

        let existingActivities = [];
        try {
            const { data } = await axiosInstance.get(`/activity/event/${id}`);
            existingActivities = data?.activities || data || [];
        } catch (err) {
            console.error("Failed to load activities:", err.response?.data || err);
        }

        setOriginalActivityIds(existingActivities.map((a) => a._id));
        setForm({
            name: event.name || "",
            hallsNumber: event.hallsNumber ?? "",
            tentsNumber: event.tentsNumber ?? "",
            buildUpStart: toInputDate(event.buildUp?.start),
            buildUpEnd: toInputDate(event.buildUp?.end),
            showDaysStart: toInputDate(event.showDays?.start),
            showDaysEnd: toInputDate(event.showDays?.end),
            teardownStart: toInputDate(event.teardown?.start),
            teardownEnd: toInputDate(event.teardown?.end),
            customFields: event.customFields?.length ? event.customFields : defaultExcelFields.map((f) => ({ ...f })),
            hasRetroPlan: event.hasRetroPlan ?? false,
            activities: existingActivities.length
                ? existingActivities.map((a) => ({ _id: a._id, key: a.key || null, name: a.name, percent: a.percent }))
                : defaultActivities.map((a) => ({ ...a, percent: 0 })),
            electricity: event.electricity ?? true,
            internet: event.internet ?? true,
        });
        setLoadingEdit(null);
        setModal({ mode: "edit", event });
    };

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return;

        const payload = {
            name: form.name.trim(),
            hallsNumber: form.hallsNumber !== "" ? Number(form.hallsNumber) : undefined,
            tentsNumber: form.tentsNumber !== "" ? Number(form.tentsNumber) : undefined,
            buildUp: { start: form.buildUpStart || undefined, end: form.buildUpEnd || undefined },
            showDays: { start: form.showDaysStart || undefined, end: form.showDaysEnd || undefined },
            teardown: { start: form.teardownStart || undefined, end: form.teardownEnd || undefined },
            customFields: (form.customFields || []).filter((f) => f.name?.trim()),
            hasRetroPlan: form.hasRetroPlan,
            electricity: form.electricity,
            internet: form.internet,
        };

        try {
            console.log("activities sent:", form.activities.map((a) => ({ name: a.name, percent: a.percent })));
            setSaving(true);
            let savedEvent;
            let id;

            if (modal.mode === "edit") {
                id = eventId(modal.event);
                const { data } = await axiosInstance.put(`/event/${id}`, payload);
                savedEvent = data?.data || data?.event || data;

                const currentIds = form.activities.filter((a) => a._id).map((a) => a._id);
                const removedIds = originalActivityIds.filter((aid) => !currentIds.includes(aid));

                const activityRequests = form.activities.map((activity, index) => {
                    const activityPayload = { name: activity.name, percent: Number(activity.percent), event: id, order: index };
                    return activity._id
                        ? axiosInstance.put(`/activity/${activity._id}`, activityPayload)
                        : axiosInstance.post("/activity", activityPayload);
                });
                const deleteRequests = removedIds.map((aid) => axiosInstance.delete(`/activity/${aid}`));

                await Promise.all([...activityRequests, ...deleteRequests]);
                setEvents((prev) => prev.map((ev) => (eventId(ev) === id ? savedEvent : ev)));
            } else {
                const { data } = await axiosInstance.post("/event", payload);
                savedEvent = data?.data || data?.event || data;
                id = eventId(savedEvent);

                const activityRequests = form.activities.map((activity, index) =>
                    axiosInstance.post("/activity", { name: activity.name, percent: Number(activity.percent), event: id, order: index })
                );
                await Promise.all(activityRequests);
                setEvents((prev) => [savedEvent, ...prev]);
            }

            setOriginalActivityIds([]);
            setModal(null);
            toast.success(modal.mode === "edit" ? "Event updated" : "Event created");
        } catch (err) {
            console.error("Failed to save event:", err.response?.data || err);
            toast.error(err.response?.data?.message || "Failed to save event");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        const id = eventId(deletingEvent);
        try {
            await axiosInstance.delete(`/event/${id}`);
            setEvents((prev) => prev.filter((e) => eventId(e) !== id));
            toast.success("Event deleted");
        } catch (err) {
            console.error("Failed to delete event:", err.response?.data || err);
            toast.error(err.response?.data?.message || "Failed to delete event");
        } finally {
            setDeletingEvent(null);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            {/* Hero */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#051948] via-[#0a2666] to-[#051948]">
                <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/5 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-sky-400/10 blur-3xl" />

                <div className="relative mx-auto flex max-w-7xl flex-col gap-7 px-4 pb-16 pt-7 sm:px-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex h-11 items-center rounded-xl bg-white px-3 shadow-lg shadow-black/10">
                                <img src={logo} alt="Expo Pilot" className="h-7 w-auto" />
                            </div>
                            <div className="h-8 w-px bg-white/15" />
                            <div>
                                <h1 className="text-xl font-semibold tracking-tight text-white">Expo Pilot</h1>
                                <p className="text-xs text-white/50">Manage every event from one place</p>
                            </div>
                        </div>
                        <button
                            onClick={openCreate}
                            disabled={!canCreateEvent}
                            title={!canCreateEvent ? "Event limit reached" : ""}
                            className="flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#051948] shadow-lg shadow-black/10 transition hover:-translate-y-0.5 hover:bg-white/95 active:translate-y-0 disabled:translate-y-0 disabled:cursor-not-allowed disabled:bg-white/20 disabled:text-white/50 disabled:shadow-none"
                        >
                            <FiPlus className="h-4 w-4" /> New event
                        </button>
                    </div>

                    {!loading && (
                        <div className="">
                            <div className="">
                                <QuotaBar used={events.length} limit={eventLimit} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <main className="relative mx-auto -mt-7 max-w-7xl px-4 pb-12 sm:px-6">
                {!canCreateEvent && <LimitBanner limit={eventLimit} />}
                {canCreateEvent && nearLimit && (
                    <LimitBanner limit={eventLimit} warning remaining={eventLimit - events.length} />
                )}

                {/* Toolbar */}
                <div className="mb-6 mt-4 flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-sm">
                    <div className="relative max-w-sm flex-1">
                        <FiSearch className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search events"
                            className="w-full rounded-xl border border-transparent bg-neutral-50 py-2.5 pl-10 pr-9 text-sm outline-none transition focus:border-[#051948]/20 focus:bg-white focus:ring-4 focus:ring-[#051948]/10"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
                                aria-label="Clear search"
                            >
                                <FiX className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                    {!loading && (
                        <span className="shrink-0 rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-600">
                            {filteredEvents.length} {filteredEvents.length === 1 ? "event" : "events"}
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
                    </div>
                ) : filteredEvents.length === 0 ? (
                    <EmptyState search={search} onCreate={openCreate} canCreateEvent={canCreateEvent} />
                ) : (
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredEvents.map((event) => (
                            <EventCard
                                key={eventId(event)}
                                event={event}
                                loadingEdit={loadingEdit === eventId(event)}
                                onManage={() => navigate(`/event/${eventId(event)}/dashboard`)}
                                onEdit={() => openEdit(event)}
                                onDelete={() => setDeletingEvent(event)}
                            />
                        ))}
                    </div>
                )}
            </main>

            {modal && (
                <EventFormModal
                    mode={modal.mode}
                    form={form}
                    setForm={setForm}
                    saving={saving}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                    onClose={() => setModal(null)}
                />
            )}

            {deletingEvent && (
                <ConfirmModal
                    title="Delete event?"
                    description={`This will permanently delete "${deletingEvent.name}" and all of its activities. This can't be undone.`}
                    confirmLabel="Delete"
                    onConfirm={handleDelete}
                    onClose={() => setDeletingEvent(null)}
                />
            )}
        </div>
    );
}

function SkeletonCard() {
    return (
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            <div className="animate-pulse space-y-4 p-5">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-neutral-100" />
                    <div className="flex-1 space-y-2">
                        <div className="h-3.5 w-2/3 rounded bg-neutral-100" />
                        <div className="h-3 w-1/2 rounded bg-neutral-100" />
                    </div>
                </div>
                <div className="h-16 w-full rounded-xl bg-neutral-100" />
                <div className="flex gap-2">
                    <div className="h-5 w-16 rounded-full bg-neutral-100" />
                    <div className="h-5 w-16 rounded-full bg-neutral-100" />
                </div>
                <div className="h-10 w-full rounded-xl bg-neutral-100" />
            </div>
        </div>
    );
}

function StatTile({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white/80">
                <Icon className="h-4 w-4" />
            </div>
            <div>
                <p className="text-xl font-semibold leading-none tabular-nums text-white">{value}</p>
                <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-white/50">{label}</p>
            </div>
        </div>
    );
}

function QuotaBar({ used, limit }) {
    const unlimited = limit === Infinity;
    const pct = unlimited ? 0 : Math.min((used / limit) * 100, 100);
    const atLimit = !unlimited && used >= limit;
    const remaining = unlimited ? null : Math.max(limit - used, 0);

    return (
        <div className="h-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm">
            <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 font-medium text-white/70">
                    <FiCalendar className="h-3.5 w-3.5" /> Event usage
                </span>
                <span className={`font-semibold tabular-nums ${atLimit ? "text-amber-300" : "text-white"}`}>
                    {unlimited ? `${used} events · Unlimited` : `${used} / ${limit}`}
                </span>
            </div>
            {!unlimited ? (
                <>
                    <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-white/10">
                        <div
                            className={`h-full rounded-full transition-all duration-700 ${atLimit ? "bg-amber-400" : "bg-gradient-to-r from-sky-300 to-white"}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <p className="mt-2 text-[11px] text-white/50">
                        {atLimit
                            ? "You've reached your limit"
                            : `${remaining} ${remaining === 1 ? "slot" : "slots"} remaining`}
                    </p>
                </>
            ) : (
                <p className="mt-2.5 text-[11px] text-white/50">No limit on your plan</p>
            )}
        </div>
    );
}

function LimitBanner({ limit, warning, remaining }) {
    return (
        <div className={`mt-4 flex items-start gap-3 rounded-2xl border px-4 py-3.5 shadow-sm ${warning ? "border-amber-200 bg-amber-50" : "border-red-200 bg-red-50"
            }`}>
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${warning ? "bg-amber-100 text-amber-600" : "bg-red-100 text-red-600"}`}>
                <FiAlertTriangle className="h-4 w-4" />
            </div>
            <div>
                <p className={`text-sm font-semibold ${warning ? "text-amber-800" : "text-red-800"}`}>
                    {warning ? "Almost at your event limit" : "Event limit reached"}
                </p>
                <p className={`mt-0.5 text-sm ${warning ? "text-amber-700" : "text-red-700"}`}>
                    {warning
                        ? `You can create ${remaining} more ${remaining === 1 ? "event" : "events"} on your current plan.`
                        : `You've used all ${limit} of your available ${limit === 1 ? "event" : "events"}. Contact support to increase your limit.`}
                </p>
            </div>
        </div>
    );
}

function EmptyState({ search, onCreate, canCreateEvent }) {
    return (
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-white py-20 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#051948]/10 to-[#051948]/5 text-[#051948]">
                {search ? <FiSearch className="h-7 w-7" /> : <FiCalendar className="h-7 w-7" />}
            </div>
            <p className="text-base font-semibold text-neutral-900">{search ? "No events found" : "No events yet"}</p>
            <p className="mx-auto mt-1.5 max-w-sm text-sm text-neutral-500">
                {search ? "Try a different search term." : "Create your first event to start managing halls, staff, and badges."}
            </p>
            {!search && canCreateEvent && (
                <button
                    onClick={onCreate}
                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#051948] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#051948]/20 transition hover:-translate-y-0.5 hover:bg-[#0a2666]"
                >
                    <FiPlus className="h-4 w-4" /> Create event
                </button>
            )}
        </div>
    );
}

function DateBlock({ label, start, end }) {
    return (
        <div className="min-w-0">
            <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                <FiClock className="h-3 w-3" /> {label}
            </p>
            <p className="mt-0.5 truncate text-xs font-medium text-neutral-700">{formatDateRange(start, end)}</p>
        </div>
    );
}

function Chip({ icon: Icon, children, className = "" }) {
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${className}`}>
            {Icon && <Icon className="h-3 w-3" />} {children}
        </span>
    );
}

function EventCard({ event, onManage, onEdit, onDelete, loadingEdit }) {
    const status = getEventStatus(event);

    return (
        <div className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:border-[#051948]/20 hover:shadow-xl hover:shadow-[#051948]/10">
            <div className="flex items-start justify-between gap-3 p-5 pb-0">
                <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#051948] to-[#0a2666] text-white shadow-md shadow-[#051948]/20">
                        <FiCalendar className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-neutral-900">{event.name}</p>
                        <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${toneClasses[status.tone]}`}>
                            {status.label}
                        </span>
                    </div>
                </div>
                <div className="flex shrink-0 gap-1 transition sm:opacity-0 sm:group-hover:opacity-100">
                    <button
                        onClick={onEdit}
                        disabled={loadingEdit}
                        className="rounded-lg p-2 text-neutral-400 transition hover:bg-neutral-100 hover:text-[#051948] disabled:opacity-50"
                        title="Edit"
                        aria-label="Edit event"
                    >
                        {loadingEdit ? (
                            <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-neutral-300 border-t-[#051948]" />
                        ) : (
                            <FiEdit2 className="h-3.5 w-3.5" />
                        )}
                    </button>
                    <button
                        onClick={onDelete}
                        className="rounded-lg p-2 text-neutral-400 transition hover:bg-red-50 hover:text-red-600"
                        title="Delete"
                        aria-label="Delete event"
                    >
                        <FiTrash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>

            <div className="flex flex-1 flex-col justify-between gap-4 p-5">
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 rounded-xl bg-neutral-50 p-3 ring-1 ring-inset ring-neutral-100">
                        <DateBlock label="Build-up" start={event.buildUp?.start} end={event.buildUp?.end} />
                        <DateBlock label="Show days" start={event.showDays?.start} end={event.showDays?.end} />
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5">
                        <Chip icon={FiMapPin} className="bg-neutral-100 text-neutral-700">
                            <span className="font-semibold">{event.hallsNumber || 0}</span> halls
                        </Chip>
                        <Chip icon={FiLayers} className="bg-neutral-100 text-neutral-700">
                            <span className="font-semibold">{event.tentsNumber || 0}</span> tents
                        </Chip>
                        {event.electricity && (
                            <Chip icon={FiZap} className="bg-emerald-50 text-emerald-700">Power</Chip>
                        )}
                        {event.internet && (
                            <Chip icon={FiWifi} className="bg-sky-50 text-sky-700">Wifi</Chip>
                        )}
                        {event.hasRetroPlan && (
                            <Chip icon={FiClipboard} className="bg-violet-50 text-violet-700">Retro plan</Chip>
                        )}
                    </div>
                </div>

                <button
                    onClick={onManage}
                    className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#051948] py-2.5 text-sm font-semibold text-white transition hover:bg-[#0a2666] active:scale-[0.98]"
                >
                    Manage event <FiChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
            </div>
        </div>
    );
}

function ModalShell({ children, wide }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#051948]/50 p-4 backdrop-blur-sm">
            <div className={`w-full ${wide ? "max-w-xl" : "max-w-sm"} overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5`}>
                {children}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="mb-1.5 block text-xs font-semibold text-neutral-600">{label}</label>
            {children}
        </div>
    );
}

function DateRange({ title, startName, endName, form, onChange }) {
    return (
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
            <p className="mb-3 text-sm font-semibold text-neutral-800">{title}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                    <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-neutral-400">Start</p>
                    <input type="datetime-local" name={startName} value={form[startName]} onChange={onChange} required className={inputClass} />
                </div>
                <div>
                    <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-neutral-400">End</p>
                    <input type="datetime-local" name={endName} value={form[endName]} onChange={onChange} required className={inputClass} />
                </div>
            </div>
        </div>
    );
}

function ToggleSwitch({ checked, onChange }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ${checked ? "bg-[#051948]" : "bg-neutral-300"}`}
        >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-6" : "translate-x-1"}`} />
        </button>
    );
}

function Card({ title, action, children }) {
    return (
        <div className="rounded-xl border border-neutral-200 bg-white p-5">
            {(title || action) && (
                <div className="mb-4 flex items-center justify-between">
                    {title && <h4 className="text-sm font-semibold text-neutral-800">{title}</h4>}
                    {action}
                </div>
            )}
            {children}
        </div>
    );
}

const STEPS = [
    { number: 1, title: "Event Info" },
    { number: 2, title: "Excel Template" },
    { number: 3, title: "Activities" },
    { number: 4, title: "Dates & Logistics" },
];

function StepIndicator({ step, onStepClick }) {
    return (
        <div className="border-b border-neutral-200 bg-neutral-50/80 px-6 py-4">
            <div className="flex items-center justify-between">
                {STEPS.map((s, idx) => (
                    <React.Fragment key={s.number}>
                        <button
                            type="button"
                            onClick={() => s.number < step && onStepClick(s.number)}
                            className={`flex min-w-0 flex-1 flex-col items-center ${s.number < step ? "cursor-pointer" : "cursor-default"}`}
                        >
                            <div
                                className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-semibold transition-all ${step > s.number
                                    ? "border-[#051948] bg-[#051948] text-white"
                                    : step === s.number
                                        ? "border-[#051948] bg-white text-[#051948] ring-4 ring-[#051948]/10"
                                        : "border-neutral-300 bg-white text-neutral-400"
                                    }`}
                            >
                                {step > s.number ? <MdCheckCircle className="text-base" /> : s.number}
                            </div>
                            <span className={`mt-1.5 text-center text-[10px] font-medium leading-tight ${step === s.number ? "text-[#051948]" : "text-neutral-400"}`}>
                                {s.title}
                            </span>
                        </button>
                        {idx < STEPS.length - 1 && (
                            <div className={`mx-1 mb-4 h-0.5 flex-1 rounded transition-all ${step > s.number ? "bg-[#051948]" : "bg-neutral-200"}`} />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

function EventFormModal({ mode, form, setForm, saving, onChange, onSubmit, onClose }) {
    const [step, setStep] = useState(1);
    const lastStep = STEPS.length;

    useEffect(() => {
        if (mode === "edit") return;
        const count = form.activities?.length || 0;
        if (count === 0) return;
        const base = Math.floor(100 / count);
        const remainder = 100 - base * count;
        const updated = form.activities.map((a, i) => ({ ...a, percent: i < remainder ? base + 1 : base }));
        const changed = updated.some((a, i) => a.percent !== form.activities[i].percent);
        if (changed) setForm((prev) => ({ ...prev, activities: updated }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.activities?.length]);

    const addCustomField = () =>
        setForm((prev) => ({ ...prev, customFields: [...(prev.customFields || []), { name: "" }] }));
    const removeCustomField = (idx) =>
        setForm((prev) => ({ ...prev, customFields: prev.customFields.filter((_, i) => i !== idx) }));
    const updateCustomField = (idx, value) =>
        setForm((prev) => ({ ...prev, customFields: prev.customFields.map((f, i) => (i === idx ? { ...f, name: value } : f)) }));
    const addActivity = () =>
        setForm((prev) => ({ ...prev, activities: [...(prev.activities || []), { key: null, name: "", percent: 0 }] }));
    const removeActivity = (idx) =>
        setForm((prev) => ({ ...prev, activities: prev.activities.filter((_, i) => i !== idx) }));
    const updateActivityName = (idx, value) =>
        setForm((prev) => ({ ...prev, activities: prev.activities.map((a, i) => (i === idx ? { ...a, name: value } : a)) }));
    const updateActivityPercent = (idx, value) =>
        setForm((prev) => ({ ...prev, activities: prev.activities.map((a, i) => (i === idx ? { ...a, percent: value === "" ? "" : Number(value) } : a)) }));

    const totalPercent = (form.activities || []).reduce((sum, a) => sum + (Number(a.percent) || 0), 0);
    const percentInvalid = mode === "create" && !form.hasRetroPlan && totalPercent !== 100;
    const canGoNext = (step !== 1 || form.name.trim().length > 0) && (step !== 3 || !percentInvalid);

    const goNext = (e) => {
        e.preventDefault();
        if (canGoNext && step < lastStep) setStep(step + 1);
    };

    const goBack = (e) => {
        e.preventDefault();
        if (step > 1) setStep(step - 1);
        else onClose();
    };

    const handleFormKeyDown = (e) => {
        // prevent Enter (e.g. inside an activity/field input) from submitting before the last step
        if (e.key === "Enter" && step < lastStep) {
            e.preventDefault();
        }
    };

    return (
        <ModalShell wide>
            <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#051948] to-[#0a2666] text-white shadow-md shadow-[#051948]/20">
                        <FiCalendar className="h-4 w-4" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#051948]">{mode === "edit" ? "Edit event" : "New event"}</h2>
                        <p className="text-xs text-neutral-400">Step {step} of {lastStep} · {STEPS[step - 1].title}</p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    disabled={saving}
                    className="rounded-lg p-2 text-neutral-400 transition hover:bg-neutral-100 hover:text-[#051948]"
                    aria-label="Close"
                >
                    <FiX className="h-5 w-5" />
                </button>
            </div>

            <StepIndicator step={step} onStepClick={setStep} />

            <form onSubmit={onSubmit} onKeyDown={handleFormKeyDown} className="flex max-h-[62vh] flex-col">
                <div className="flex-1 space-y-4 overflow-y-auto bg-neutral-50/50 px-6 py-5">
                    {step === 1 && (
                        <Card title="Event details">
                            <Field label="Event name">
                                <input name="name" required autoFocus value={form.name} onChange={onChange} placeholder="ABC Event 2026" className={inputClass} />
                            </Field>
                        </Card>
                    )}

                    {step === 2 && (
                        <div className="space-y-4">
                            <Card title="Default Excel template fields">
                                <div className="overflow-hidden rounded-xl border border-neutral-200">
                                    <table className="w-full text-sm">
                                        <thead className="bg-neutral-50">
                                            <tr><th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Field name</th></tr>
                                        </thead>
                                        <tbody>
                                            {defaultExcelFields.map((f, i) => (
                                                <tr key={i} className="border-t border-neutral-100">
                                                    <td className="px-4 py-2.5 text-neutral-700">{f.name}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="mt-2.5 text-xs text-neutral-500">These default fields are always included and don't need to be added separately.</p>
                            </Card>

                            <Card
                                title="Additional custom fields"
                                action={
                                    (form.customFields || []).length > defaultExcelFields.length && (
                                        <span className="rounded-full bg-[#051948]/5 px-2.5 py-0.5 text-xs font-semibold text-[#051948]">
                                            {form.customFields.length - defaultExcelFields.length} added
                                        </span>
                                    )
                                }
                            >
                                {(form.customFields || []).filter((_, i) => i >= defaultExcelFields.length).length === 0 ? (
                                    <p className="rounded-xl border border-dashed border-neutral-200 py-5 text-center text-xs text-neutral-400">
                                        No custom fields yet — add anything specific to this event's Excel export.
                                    </p>
                                ) : (
                                    <div className="space-y-2.5">
                                        {(form.customFields || []).filter((_, i) => i >= defaultExcelFields.length).map((field, i) => {
                                            const idx = i + defaultExcelFields.length;
                                            return (
                                                <div key={idx} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-2.5 transition focus-within:border-[#051948]/40 focus-within:bg-white">
                                                    <input
                                                        placeholder="Field label (e.g. Company, Role, Badge Type)"
                                                        value={field.name}
                                                        onChange={(e) => updateCustomField(idx, e.target.value)}
                                                        className="flex-1 bg-transparent text-sm font-medium text-neutral-700 outline-none placeholder-neutral-400"
                                                    />
                                                    <button type="button" onClick={() => removeCustomField(idx)} className="text-neutral-400 transition hover:text-red-600" title="Remove">
                                                        <FiTrash2 size={15} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={addCustomField}
                                    className="mt-3 w-full rounded-xl border border-dashed border-[#051948]/30 py-2.5 text-sm font-semibold text-[#051948] transition hover:bg-[#051948]/5"
                                >
                                    + Add custom field
                                </button>
                            </Card>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4">
                            <Card>
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-semibold text-neutral-800">Has retro plan</p>
                                        <p className="mt-0.5 text-xs text-neutral-500">
                                            {form.hasRetroPlan
                                                ? "The stand contractor receives a link and fills in what he does on each date."
                                                : mode === "edit"
                                                    ? "Percentages are set at creation and can't be changed."
                                                    : "Percentages are split evenly by default. You can adjust them, but the total must be exactly 100%."}
                                        </p>
                                    </div>
                                    <ToggleSwitch checked={form.hasRetroPlan} onChange={(val) => setForm((prev) => ({ ...prev, hasRetroPlan: val }))} />
                                </div>
                            </Card>

                            <Card
                                title="Activities"
                            >
                                <div className="space-y-2.5">
                                    {(form.activities || []).map((activity, idx) => (
                                        <div key={activity._id || activity.key || idx} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-2.5">
                                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-neutral-500 ring-1 ring-neutral-200">
                                                {idx + 1}
                                            </span>
                                            <input
                                                value={activity.name}
                                                onChange={(e) => updateActivityName(idx, e.target.value)}
                                                placeholder="Activity name"
                                                className="flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-[#051948] focus:ring-4 focus:ring-[#051948]/10"
                                            />
                                            {!form.hasRetroPlan && (
                                                <div className="flex shrink-0 items-center gap-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={activity.percent}
                                                        disabled={mode === "edit"}
                                                        onChange={(e) => updateActivityPercent(idx, e.target.value)}
                                                        className="w-16 rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-[#051948] outline-none transition focus:border-[#051948] focus:ring-4 focus:ring-[#051948]/10 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500"
                                                    />
                                                    <span className="text-xs text-neutral-400">%</span>
                                                </div>
                                            )}
                                            <button type="button" onClick={() => removeActivity(idx)} className="pr-1 text-neutral-400 transition hover:text-red-600" title="Remove">
                                                <FiTrash2 size={15} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {mode === "create" && !form.hasRetroPlan && (
                                    <div className={`mt-3 flex items-center justify-between rounded-xl border px-3.5 py-2.5 text-xs font-medium ${percentInvalid ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                                        <span>
                                            {percentInvalid
                                                ? totalPercent > 100
                                                    ? `Over by ${totalPercent - 100}%. Total must be exactly 100%.`
                                                    : `${100 - totalPercent}% remaining. Total must be exactly 100%.`
                                                : "Total is 100%"}
                                        </span>
                                        <span className="font-semibold tabular-nums">{totalPercent}%</span>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={addActivity}
                                    className="mt-3 w-full rounded-xl border border-dashed border-[#051948]/30 py-2.5 text-sm font-semibold text-[#051948] transition hover:bg-[#051948]/5"
                                >
                                    + Add activity
                                </button>
                            </Card>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-4">
                            <DateRange title="Build-up" startName="buildUpStart" endName="buildUpEnd" form={form} onChange={onChange} />
                            <DateRange title="Show days" startName="showDaysStart" endName="showDaysEnd" form={form} onChange={onChange} />
                            <DateRange title="Teardown" startName="teardownStart" endName="teardownEnd" form={form} onChange={onChange} />

                            <Card title="Event logistics">
                                <div className="mb-4 grid grid-cols-2 gap-4">
                                    <Field label="Halls"><input type="number" min="0" name="hallsNumber" value={form.hallsNumber} onChange={onChange} className={inputClass} /></Field>
                                    <Field label="Tents"><input type="number" min="0" name="tentsNumber" value={form.tentsNumber} onChange={onChange} className={inputClass} /></Field>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between rounded-xl border border-neutral-200 bg-neutral-50 p-3.5">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                                                <FiZap className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-neutral-800">Electricity available</p>
                                                <p className="mt-0.5 text-xs text-neutral-500">Power supply for stands & halls</p>
                                            </div>
                                        </div>
                                        <ToggleSwitch checked={form.electricity} onChange={(val) => setForm((prev) => ({ ...prev, electricity: val }))} />
                                    </div>
                                    <div className="flex items-center justify-between rounded-xl border border-neutral-200 bg-neutral-50 p-3.5">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
                                                <FiWifi className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-neutral-800">Internet available</p>
                                                <p className="mt-0.5 text-xs text-neutral-500">WiFi or wired connection</p>
                                            </div>
                                        </div>
                                        <ToggleSwitch checked={form.internet} onChange={(val) => setForm((prev) => ({ ...prev, internet: val }))} />
                                    </div>
                                </div>
                            </Card>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-neutral-200 bg-white px-6 py-4">
                    <button
                        type="button"
                        onClick={goBack}
                        disabled={saving}
                        className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
                    >
                        {step > 1 ? "← Back" : "Cancel"}
                    </button>

                    {step < lastStep ? (
                        <button
                            type="button"
                            onClick={goNext}
                            disabled={!canGoNext}
                            className="rounded-xl bg-[#051948] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0a2666] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Next →
                        </button>
                    ) : (
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 rounded-xl bg-[#051948] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0a2666] disabled:opacity-60"
                        >
                            {saving && <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />}
                            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create event"}
                        </button>
                    )}
                </div>
            </form>
        </ModalShell>
    );
}

function ConfirmModal({ title, description, confirmLabel, onConfirm, onClose }) {
    const [confirming, setConfirming] = useState(false);

    const handleConfirm = async () => {
        setConfirming(true);
        await onConfirm();
        setConfirming(false);
    };

    return (
        <ModalShell>
            <div className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600 ring-8 ring-red-50/60">
                    <FiTrash2 className="h-5 w-5" />
                </div>
                <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-neutral-500">{description}</p>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} disabled={confirming} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-50">
                        Cancel
                    </button>
                    <button onClick={handleConfirm} disabled={confirming} className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60">
                        {confirming ? "Deleting…" : confirmLabel}
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}